import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';
import {debounce} from 'lodash-es';

import {
    Box,
    Divider,
    Flex,
    Heading,
    Tabs,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Text,
    SkeletonText,
    Checkbox,
    Button,
} from '@spothero/ui';

import {
    clearAppliedFilters,
    ERRORS,
    getSearchedFacilitiesData,
    setFacilitySearchText,
    setFilteredFacilities,
    updateAppliedFilters,
    getPaginatedFacilitiesData,
} from 'pages/facilities/store/facilities-actions';
import {
    selectAllCanonicalFacilities,
    selectAllFilteredFacilities,
    selectFacilityEntities,
    selectSelectedFacilityId,
    selectAppliedFilters,
    selectFacilityIds,
    selectFilteredFacilityIds,
    selectSelectedFacility,
} from 'pages/facilities/store/facilities-selectors';
import {useFacilityMultiSelect} from '../context/FacilityMultisSelectContext';

import PortfolioViewBanner from '../PortfolioViewBanner';
import FacilitiesList from '../facilities-list/FacilitiesList';

import FacilitySearch from './FacilitySearch';
import FiltersView from './FiltersView';

const searchWindowHeadingStyleProps = {
    fontWeight: 'semibold',
    marginRight: 1,
    paddingY: 2,
    paddingBottom: 3.5,
    paddingTop: 5,
};

const defaultPaginationLimit = 25;
const defaultIncreasedPaginationLimit = 1000;

const SearchWindow = ({
    areFiltersOpen,
    containerRef,
    onFiltersToggle,
    tabIndex,
    setTabIndex,
}) => {
    const dispatch = useDispatch();
    const selectedFacility = useSelector(selectSelectedFacility);
    const filteredFacilities = useSelector(selectAllFilteredFacilities);
    const selectedFacilityId = useSelector(selectSelectedFacilityId);
    const canonicalFacilities = useSelector(selectAllCanonicalFacilities);
    const facilityEntities = useSelector(selectFacilityEntities);
    const facilityIds = useSelector(selectFacilityIds);
    const filteredFacilityIds = useSelector(selectFilteredFacilityIds);
    const {meta, error} = useSelector((state) => state.facilities);
    const {isLoading} = useSelector((state) => state.facilities);
    const isPaginating = useSelector(
        (state) => state.facilities.facilities.isLoading
    );
    const appliedFilters = useSelector(selectAppliedFilters);
    const searchText = useSelector(
        ({facilities}) => facilities.appliedFilters.searchText
    );
    const [allFacilitiesSelected, setAllFacilitiesSelected] = useState(
        filteredFacilityIds.length === facilityIds.length
    );
    const [scrollPosition, setScrollPosition] = useState(0);
    const numFiltersApplied =
        appliedFilters.cities.length +
        appliedFilters.status.length +
        (appliedFilters.operatorEmail ? 1 : 0);
    const facilitiesCount = meta.totalFacilityCount;
    const onTabChange = (nextIndex) => setTabIndex(nextIndex);
    const isMultiSelectEnabled = useFacilityMultiSelect();
    const hidSearchResultsText =
        !isMultiSelectEnabled || error === ERRORS.NO_RESULTS_FOUND;
    const selectedFacilities = isMultiSelectEnabled
        ? filteredFacilities
        : selectedFacilityId
          ? [selectedFacility]
          : [];

    const loadMoreRows = () => {
        dispatch(
            getPaginatedFacilitiesData({
                limit: defaultPaginationLimit,
                offset: meta.nextOffset,
                searchText,
                filters: appliedFilters,
            })
        ).unwrap();
    };

    const handleClearAllFilters = () => {
        dispatch(clearAppliedFilters());

        dispatch(
            getSearchedFacilitiesData({
                limit: defaultPaginationLimit,
                searchText,
            })
        );
    };

    const handleChipClick = (value, type) => {
        const updatedFilters = {...appliedFilters};

        if (type === 'operatorEmail') {
            updatedFilters.operatorEmail = '';
        } else {
            updatedFilters[type] = appliedFilters[type].filter(
                (filter) => filter !== value
            );
        }

        dispatch(updateAppliedFilters(updatedFilters));

        dispatch(
            getSearchedFacilitiesData({
                limit: defaultPaginationLimit,
                searchText,
                filters: updatedFilters,
            })
        );
    };

    const onClearSearch = () => {
        dispatch(setFacilitySearchText(''));
        dispatch(
            getSearchedFacilitiesData({
                limit: defaultPaginationLimit,
                searchText: '',
                filters: appliedFilters,
            })
        );
    };

    const onSearchChange = useCallback(
        (value, bypassTrim = false) => {
            if (!bypassTrim) {
                const trimmedValue = value.trim();

                if (!trimmedValue) {
                    return;
                }
            }

            dispatch(setFacilitySearchText(value));
            // Dispatch the search API call with the search value
            dispatch(
                getSearchedFacilitiesData({
                    limit: defaultPaginationLimit,
                    searchText: value,
                    filters: appliedFilters,
                })
            );
        },
        [appliedFilters, dispatch]
    );

    const handleSelectAllFacilities = useCallback(async () => {
        setAllFacilitiesSelected(!allFacilitiesSelected);

        // deselect if all selected
        if (allFacilitiesSelected) {
            return dispatch(setFilteredFacilities([]));
        }

        // for the "operator" experience load all the facilities if the total count is less than 1000
        if (meta.nextOffset && meta.totalFilteredFacilityCount <= 1000) {
            const facilityData = await dispatch(
                getPaginatedFacilitiesData({
                    limit: defaultIncreasedPaginationLimit,
                    offset: meta.nextOffset,
                    searchText,
                    filters: appliedFilters,
                })
            ).unwrap();

            await dispatch(
                setFilteredFacilities([
                    ...filteredFacilityIds,
                    ...facilityIds,
                    ...Object.keys(facilityData.entities.parkingSpots),
                ])
            ).unwrap();

            return;
        }

        // select all facilities
        return dispatch(
            setFilteredFacilities([...filteredFacilityIds, ...facilityIds])
        );
    }, [
        allFacilitiesSelected,
        meta.nextOffset,
        meta.totalFilteredFacilityCount,
        facilityIds,
        dispatch,
        filteredFacilityIds,
        searchText,
        appliedFilters,
    ]);

    const handlePaginationLimitChange = async () => {
        await dispatch(
            getPaginatedFacilitiesData({
                limit: defaultIncreasedPaginationLimit,
                offset: meta.nextOffset,
                searchText,
                filters: appliedFilters,
            })
        ).unwrap();
    };

    const debouncedSearch = useMemo(() => {
        return debounce(onSearchChange, 750);
    }, [onSearchChange]);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <>
            <Box data-testid="SearchWindow">
                <Flex marginLeft={4} position="relative">
                    {isMultiSelectEnabled && <PortfolioViewBanner />}
                    <Heading as="h4" {...searchWindowHeadingStyleProps}>
                        My Facilities ({facilitiesCount})
                    </Heading>
                </Flex>
                <Tabs
                    isLazy
                    isManual
                    size="small"
                    variant="enclosed"
                    index={tabIndex}
                    onChange={onTabChange}
                >
                    <TabList marginX={4} marginBottom={tabIndex === 0 ? 0 : 4}>
                        <Tab width="100%">List (All)</Tab>
                        <Tab width="100%">
                            Selected ({selectedFacilities.length})
                        </Tab>
                    </TabList>
                    {tabIndex === 0 && (
                        <>
                            <FacilitySearch
                                onChange={debouncedSearch}
                                onSubmit={(value) =>
                                    debouncedSearch(value, true)
                                }
                                onClear={onClearSearch}
                                numFiltersApplied={numFiltersApplied}
                                onFiltersToggle={onFiltersToggle}
                                appliedCities={appliedFilters.cities}
                                appliedStatus={appliedFilters.status}
                                label="Search Facilities"
                                defaultValue={searchText}
                            />
                            <Box
                                borderColor="gray.200"
                                borderTopWidth={1}
                                paddingY={4}
                                paddingX={4}
                                backgroundColor="white"
                            >
                                {isLoading ? (
                                    <SkeletonText
                                        noOfLines={1}
                                        spacing="2"
                                        skeletonHeight="4"
                                        width="50%"
                                    />
                                ) : (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Box>
                                            {!hidSearchResultsText &&
                                                (isLoading ? (
                                                    <SkeletonText
                                                        noOfLines={1}
                                                        spacing="2"
                                                        skeletonHeight="4"
                                                        width="100%"
                                                    />
                                                ) : (
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                    >
                                                        <Checkbox
                                                            onChange={
                                                                handleSelectAllFacilities
                                                            }
                                                            size="md"
                                                            isChecked={
                                                                allFacilitiesSelected ||
                                                                filteredFacilityIds.length >
                                                                    1
                                                            }
                                                            isIndeterminate={
                                                                filteredFacilityIds.length <
                                                                    meta.totalFilteredFacilityCount &&
                                                                filteredFacilityIds.length >
                                                                    1
                                                            }
                                                            isDisabled={
                                                                isPaginating
                                                            }
                                                            alignItems="center"
                                                        >
                                                            <Text
                                                                fontSize="xs"
                                                                variant="body2"
                                                                fontWeight="semibold"
                                                            >
                                                                {allFacilitiesSelected
                                                                    ? `Deselect facilities`
                                                                    : `Select ${
                                                                          meta.totalFacilityCount <=
                                                                          defaultIncreasedPaginationLimit
                                                                              ? 'all'
                                                                              : facilityIds.length
                                                                      } facilities`}
                                                            </Text>
                                                        </Checkbox>
                                                        {meta.totalFacilityCount >
                                                            1000 &&
                                                            facilityIds.length <
                                                                meta.totalFacilityCount && (
                                                                <Button
                                                                    variant="tertiary"
                                                                    onClick={
                                                                        handlePaginationLimitChange
                                                                    }
                                                                    fontSize="xs"
                                                                    isLoading={
                                                                        isPaginating
                                                                    }
                                                                    loadingText="Loading..."
                                                                    spinnerPlacement="end"
                                                                    justifyContent="flex-start"
                                                                    marginTop={
                                                                        2
                                                                    }
                                                                >
                                                                    Load 1000
                                                                    more
                                                                </Button>
                                                            )}
                                                    </Box>
                                                ))}
                                        </Box>
                                        <Text
                                            fontSize="xs"
                                            color="text.secondary.light"
                                            fontWeight="semibold"
                                            marginRight={2}
                                        >
                                            {`${meta.totalFilteredFacilityCount} results`}
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                            <Divider />
                        </>
                    )}

                    <TabPanels>
                        <TabPanel
                            data-testid="FacilityList-TabPanel-SelectAll"
                            padding={0}
                        >
                            <FacilitiesList
                                facilities={canonicalFacilities}
                                facilityEntities={facilityEntities}
                                loadMoreRows={loadMoreRows}
                                isMultiSelectEnabled={isMultiSelectEnabled}
                                showCanonicalGroups
                                meta={meta}
                                errorMessage={error}
                                isLoading={isLoading}
                                setScrollPosition={setScrollPosition}
                            />
                        </TabPanel>
                        <TabPanel
                            data-testid="FacilityList-TabPanel-Selected"
                            padding={0}
                        >
                            <FacilitiesList
                                facilities={selectedFacilities}
                                facilityEntities={facilityEntities}
                                isMultiSelectEnabled={isMultiSelectEnabled}
                                meta={meta}
                                errorMessage={error}
                                isLoading={isLoading}
                                setScrollPosition={setScrollPosition}
                                scrollPosition={scrollPosition} // TODO: We might need to use this at some point
                                selectedTabActive
                            />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>

            <FiltersView
                areFiltersOpen={areFiltersOpen}
                containerRef={containerRef}
                onFiltersToggle={onFiltersToggle}
                handleClearAllFilters={handleClearAllFilters}
                handleChipClick={handleChipClick}
                numFiltersApplied={numFiltersApplied}
            />
        </>
    );
};

SearchWindow.propTypes = {
    areFiltersOpen: PropTypes.bool.isRequired,
    containerRef: PropTypes.object,
    onFiltersToggle: PropTypes.func.isRequired,
    tabIndex: PropTypes.number.isRequired,
    setTabIndex: PropTypes.func.isRequired,
};

export default SearchWindow;
